"use client";
import Icon from "@/components/atoms/Icons";
import Image from "@/components/atoms/image";
import { ReactNode } from "react";
import { motion } from "framer-motion";
import Heading from "@/components/molecules/Heading";
import { ImageData } from "@/page-builder/fragments/StatsCardsBlock/types";
import { Note } from "./Note";

export interface ResultGridProps {
  title: ReactNode;
  subTitle: ReactNode;
  items: {
    title: string;
    theme: string;
    image: ImageData;
    description: string;
  }[];
}

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.2,
      ease: "easeOut",
    },
  }),
};

const getCard = (
  style: "Red" | "orange" | "greenDark" | "greenLight",
  title: string,
  url: string,
  alt: string,
  text: string
) => {
  switch (style) {
    case "orange":
      return (
        <div className="bg-[#FEF8F6] flex flex-col w-full">
          <div className="z-10 pt-[32px] pb-[32px] pl-[32px] pr-[32px] md:pt-[44px] md:pb-[44px] xl:pl-[44px] xl:pr-[44px] gap-[8px] flex flex-col flex-1">
            <div
              className="mb-auto relative flex items-center justify-center w-[120px] h-[120px]"
              aria-hidden="true"
            >
              <div className="absolute w-full h-full">
                <Icon name="fig16" />
              </div>
              <div className="absolute top-[30px] left-[30px] w-[60px] h-[60px]">
                {url && <Image fill alt={`${alt}`} src={`${url}`} />}
              </div>
            </div>
            <span className="text-neutral-900 text-xl-bold md:text-2xl-bold leading-[36px] md:leading-[48px]">
              {title}
            </span>
            <span className="text-neutral-900 text-l-regular leading-[32px]">
              {text}
            </span>
          </div>

          <div
            className="absolute z-0 flex xl:bottom-[-40px] right-0 bottom-[-80px]"
            aria-hidden="true"
          >
            <Icon name="fig4" />
          </div>
        </div>
      );
    case "greenDark":
      return (
        <div className="bg-[#BFD9C8] flex flex-col w-full">
          <div className="z-10 pt-[32px] pb-[32px] pl-[32px] pr-[32px] md:pt-[44px] md:pb-[44px] xl:pl-[44px] xl:pr-[44px] gap-[8px] flex flex-col flex-1">
            <div
              className="mb-auto relative flex items-center justify-center w-[120px] h-[120px]"
              aria-hidden="true"
            >
              <div className="absolute w-full h-full">
                <Icon name="fig17" />
              </div>
              <div className="absolute top-[36px] left-[30px] w-[60px] h-[60px]">
                {url && <Image fill alt={`${alt}`} src={`${url}`} />}
              </div>
            </div>
            <span className="text-neutral-900 text-xl-bold md:text-2xl-bold leading-[36px] md:leading-[48px]">
              {title}
            </span>
            <span className="text-neutral-900 text-l-regular leading-[32px]">
              {text}
            </span>
          </div>

          <div className="absolute flex md:hidden xl:flex  bottom-[10px] right-[-100px] w-[253px] h-[83px]">
            <Icon name="fig15" />
          </div>
          <div className="absolute hidden md:flex xl:hidden bottom-[35px] right-[0px] w-[303px] h-[63px]">
            <Icon name="fig14" />
          </div>
        </div>
      );
    default:
      return (
        <div className="bg-[#FADACC] flex flex-col w-full">
          <div className="z-10 pt-[32px] pb-[32px] pl-[32px] pr-[32px] md:pt-[44px] md:pb-[44px] xl:pl-[44px] xl:pr-[44px] gap-[8px] flex flex-col flex-1">
            <div
              className="mb-auto relative flex items-center justify-center w-[120px] h-[120px]"
              aria-hidden="true"
            >
              <div className="absolute w-full h-full">
                <Icon name="fig18" />
              </div>
              <div className="absolute top-[30px] left-[30px] w-[60px] h-[60px]">
                {url && <Image fill alt={`${alt}`} src={`${url}`} />}
              </div>
            </div>{" "}
            <span className="text-neutral-900 text-xl-bold md:text-2xl-bold leading-[36px] md:leading-[48px]">
              {title}
            </span>
            <span className="text-neutral-900 text-l-regular leading-[32px]">
              {text}
            </span>
          </div>

          <div className="absolute bottom-[-8px] right-[102px] md:right-[252px] xl:right-[152px] w-[153px] h-[50px]">
            <Icon name="fig13" />
          </div>
        </div>
      );
  }
};

export const ResultGrid = ({
  items = [],
  title = "",
  subTitle = "",
}: ResultGridProps) => {
  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 gap-[32px]">
        <div className="">
          <Heading title={title} />
        </div>
        <div className="grid grid-cols-3 gap-[32px] overflow-hidden">
          {items.map((e, i) => {
            const customIndex = i % 3;

            let className = "";
            switch (customIndex) {
              case 0:
                className =
                  "relative mb-0 xl:mb-[64px] overflow-hidden col-span-3 xl:col-span-1 flex w-full xl:min-h-[360px] md:min-h-[328px] min-h-[323px]";
                break;
              case 1:
                className =
                  "relative mt-0 xl:mt-[32px] mb-0 xl:mb-[32px] overflow-hidden col-span-3 xl:col-span-1 flex w-full xl:min-h-[360px] md:min-h-[328px] min-h-[323px]";
                break;
              case 2:
                className =
                  "relative mt-0 xl:mt-[64px] overflow-hidden col-span-3 xl:col-span-1 flex w-full xl:min-h-[360px] md:min-h-[328px] min-h-[323px]";
                break;
            }

            return (
              <motion.div
                variants={fadeUpVariants as any}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={customIndex}
                key={i}
                className={className}
              >
                {getCard(
                  e.theme as "Red" | "orange" | "greenDark" | "greenLight",
                  e.title,
                  e.image.url,
                  e.image.alt,
                  e.description
                )}
              </motion.div>
            );
          })}
        </div>
        <div className="text-center mx-auto">
          <Note title={subTitle} />
        </div>
      </div>
    </div>
  );
};
